import { useCallback, useEffect, useState } from 'react'

import * as PIXI from 'pixi.js'

import { useMapEventBus } from 'modules/space_control/contexts/workSpacesMap/MapEventBusContext'
import { WorkSpaceWithBookingNode } from 'modules/space_control/hooks/useWorkSpacesWithBookings'
import { usePIXI } from 'modules/space_control/lib/canvas'
import { Viewport } from 'modules/space_control/lib/canvas/viewport'
import { EVENT, UpdateWorkspacesEvent } from 'modules/space_control/lib/workSpacesMap/mapEvents'

import { Pin } from './pin.pixi'

interface AppProps {
  mapImage: { url?: string | null } | null | undefined
}

export const App = ({ mapImage }: AppProps) => {
  const app = usePIXI()
  const [pinsContainer, setPinsContainer] = useState<PIXI.Container>()
  const [viewport, setViewport] = useState<PIXI.Container | null>(null)
  const [currentWorkSpaces, setCurrentWorkSpaces] = useState<WorkSpaceWithBookingNode[]>([])
  const { eventBus, setReady } = useMapEventBus()

  useEffect(() => {
    if (!eventBus) return

    const handleUpdateWorkspaces = ({ workSpaces }: UpdateWorkspacesEvent) => {
      setCurrentWorkSpaces(workSpaces)
    }

    eventBus.on(EVENT.UPDATE_WORKSPACES, handleUpdateWorkspaces)
    setReady(true)

    return () => {
      eventBus.off(EVENT.UPDATE_WORKSPACES, handleUpdateWorkspaces)
      setReady(false)
    }
  }, [eventBus, setReady])

  useEffect(() => {
    if (!app || !mapImage || !mapImage?.url) return

    const viewportMask = new PIXI.Graphics()
    viewportMask.beginFill(0x000000, 0)
    viewportMask.drawRect(0, 0, app.screen.width, app.screen.height)
    viewportMask.endFill()

    viewportMask.interactive = true
    viewportMask.hitArea = new PIXI.Rectangle(0, 0, app.screen.width, app.screen.height)

    app.stage.addChild(viewportMask)
    app.stage.interactiveChildren = true

    const newViewport = Viewport(app, {
      isPanEnabled: true,
      isPinchZoomEnabled: true,
      isWheelZoomEnabled: true,
    })

    const image = PIXI.Sprite.from(mapImage.url)
    image.anchor.set(0.5)
    newViewport.addChild(image)

    const pins = new PIXI.Container()
    setPinsContainer(pins)
    newViewport.addChild(pins)

    viewportMask.addChild(newViewport)
    setViewport(newViewport)

    return () => {
      newViewport.unbind()
      newViewport.destroy({ children: true })
    }
  }, [app, mapImage])

  useEffect(() => {
    if (!viewport || !eventBus || !app?.view) return

    const canvasElement = app.view
    if (!(canvasElement instanceof HTMLCanvasElement)) return

    const activateCursor = () => {
      viewport.cursor = 'move'
      canvasElement.style.cursor = 'move'
    }

    const resetCursor = () => {
      viewport.cursor = 'default'
      canvasElement.style.cursor = 'default'
    }

    eventBus.on(EVENT.ACTIVATE_MOVE_CURSOR, activateCursor)
    eventBus.on(EVENT.RESET_CURSOR, resetCursor)

    return () => {
      eventBus.off(EVENT.ACTIVATE_MOVE_CURSOR, activateCursor)
      eventBus.off(EVENT.RESET_CURSOR, resetCursor)
    }
  }, [viewport, eventBus, app?.view])

  useEffect(() => {
    if (!pinsContainer || !currentWorkSpaces || !eventBus) return

    pinsContainer.removeChildren()

    currentWorkSpaces.forEach((workSpace) => {
      if (!workSpace.mapSpaceLocation) return

      const pin = Pin(workSpace, {
        onClick: (workSpaceId) => {
          eventBus.emit(EVENT.SPACE_CLICK, { workSpaceId: +workSpaceId })
        },
        onHover: (workSpaceId) => {
          eventBus.emit(EVENT.SPACE_HOVER, { workSpaceId: +workSpaceId })
        },
        onLeave: () => {
          eventBus.emit(EVENT.SPACE_HOVER, { workSpaceId: null })
        },
      })
      if (!pin) return

      pin.x = workSpace.mapSpaceLocation.x
      pin.y = workSpace.mapSpaceLocation.y
      pinsContainer.addChild(pin)
    })

    return () => {
      pinsContainer.removeChildren()
    }
  }, [currentWorkSpaces, pinsContainer, eventBus])

  const handleViewportClick = useCallback(
    (event: PIXI.FederatedPointerEvent) => {
      if (event.button === 0 && viewport && eventBus) {
        const point = viewport.toLocal(event.global)
        eventBus.emit(EVENT.MAP_CLICK, { x: point.x, y: point.y })
      }
    },
    [viewport, eventBus]
  )

  useEffect(() => {
    if (!viewport) return

    viewport.on('click', handleViewportClick)

    return () => {
      viewport.off('click', handleViewportClick)
    }
  }, [viewport, handleViewportClick])

  return null
}
